/* eslint-disable @typescript-eslint/no-explicit-any */
import { Col, Row } from '@design-system/components/grid/grid';

import {
  Desc,
  TitleRow,
} from '../../promotions/view/components/FormTypography';
import { Errors } from '../../promotions/view/components/Errors';

import { DateInput } from '../../promotions/view/components/DateInput';
import { Textarea } from '@design-system/components/textarea/textarea';
import { Typography } from '@design-system/components/typography/typography';
import { TimeInput } from './components/TimeInput';
import { Button } from '@design-system/components/button/button';
import { Controller } from 'react-hook-form';
import { wrapper } from './components/ChallengeForm.css';
import { Group } from '../../promotions/view/components/Group';
import { ChallengeFormVM } from '../view-model/ChallengeForm.viewModel';
import { ChallengesVM } from '../view-model/Challenges.viewModel';
import { InfoTip } from '@client/new/pages/Marketing/InfoTip';
import { TemplatesList } from './components/TemplatesList';
import { TextInput } from '@client/new/pages/promotions/view/components/TextInput';
import { InputLabel } from '@design-system/components/input/input';
import { ToggleDescription } from './components/ToggleDescription';
import { HelpButton } from './components/NeedHelpRow';

export const Label = (p: { children: string }) => (
  <Typography variant="body" fontWeight="medium" {...p} />
);

type ChallengeFormProps = ChallengeFormVM & {
  mode: ChallengesVM['modalMode']['type'];
  dispatch: ChallengesVM['dispatch'];
};

export const ChallengeForm = ({
  register,
  onSubmit,
  values,
  mode,
  ...p
}: ChallengeFormProps) => {
  return (
    <form onSubmit={onSubmit}>
      <Col data-testid="ChallengeForm" className={wrapper} gap="xl">
        <TitleRow
          title={mode === 'edit' ? 'Edit challenge' : 'New challenge'}
          rightContent={
            <Row gap="l">
              <HelpButton />

              <Button
                color="neutral"
                onClick={onSubmit}
                loading={Boolean(p.inProgress)}
              >
                {mode === 'edit' ? 'Save' : 'Create'}
              </Button>
            </Row>
          }
        />

        <Errors data={p.errors} />

        <TextInput
          label="Name"
          placeholder="Type Challenge name"
          {...register<string>('name')}
          required
        />
        <InfoTip>Name your challenge or leave the default name</InfoTip>

        <Group title="Registration & before photo submission">
          <Desc>
            Define when challenge registration should start and how long it’s
            open
          </Desc>

          <Col gap="xl">
            <Col style={{ gap: 0 }}>
              <InputLabel required>Start date</InputLabel>

              <Row style={{ alignItems: 'flex-start' }}>
                <DateInput
                  {...register('registrationStart.dateTimestamp')}
                  width={160}
                />
                <TimeInput
                  prefix="registrationStart"
                  register={register}
                  values={values}
                  width={160}
                />
              </Row>
            </Col>

            <Col style={{ gap: 0 }}>
              <InputLabel required>End date</InputLabel>
              <Row>
                <DateInput
                  {...register('registrationEnd.dateTimestamp')}
                  width={160}
                />
                <TimeInput
                  prefix="registrationEnd"
                  register={register}
                  values={values}
                  width={160}
                />
              </Row>
            </Col>
          </Col>
          <Typography variant="body" size="large" fontWeight="medium">
            Required fields
          </Typography>
          <Desc>
            Select which fields should be required for users to register
          </Desc>
          <Col>
            <Row gap="xl">
              {p.submissionToggles.map((option) => (
                <ToggleDescription
                  key={option.value}
                  {...option}
                  {...register(option.value)}
                />
              ))}
            </Row>
          </Col>
        </Group>

        <Group title="Completion & after photo submission">
          <Desc>
            Define when challenge completion should start and how long it’s open
          </Desc>

          <Col gap="xl">
            <Col style={{ gap: 0 }}>
              <InputLabel required>Start date</InputLabel>

              <Row style={{ alignItems: 'flex-start' }}>
                <DateInput
                  {...register('completionStart.dateTimestamp')}
                  width={160}
                />
                <TimeInput
                  prefix="completionStart"
                  register={register}
                  values={values}
                  width={160}
                />
              </Row>
            </Col>

            <Col style={{ gap: 0 }}>
              <InputLabel required>End date</InputLabel>
              <Row>
                <DateInput
                  {...register('completionEnd.dateTimestamp')}
                  width={160}
                />
                <TimeInput
                  prefix="completionEnd"
                  register={register}
                  values={values}
                  width={160}
                />
              </Row>
            </Col>
          </Col>

          <Typography variant="body" size="large" fontWeight="medium">
            Required fields
          </Typography>
          <Desc>
            Select which fields should be required for users to complete
          </Desc>

          <Col>
            <Row gap="xl">
              {p.completionToggles.map((option) => (
                <ToggleDescription
                  key={option.label}
                  {...option}
                  {...register(option.value)}
                />
              ))}
            </Row>
          </Col>
        </Group>

        <Group title="Photos section visibility">
          <Col>
            <ToggleDescription
              label="Show section"
              {...register('showPhotosSection')}
            />
          </Col>
          <InfoTip>
            Turn this toggle off to hide photo section to your users. When it’s
            off users won’t be able to upload any photos during submission.
          </InfoTip>
        </Group>
        <Group title="Photo Guidelines">
          <Desc>
            Customize the guidelines for before/after photo submissions.{' '}
            <a
              href="https://www.markdownguide.org/basic-syntax/"
              target="_blank"
              rel="noreferrer"
            >
              Markdown
            </a>{' '}
            formatting can be used.
          </Desc>
          <Controller
            name="photoInstructions"
            control={p.control}
            render={({ field }) => (
              <Textarea {...field} placeholder="Enter instructions" rows={4} />
            )}
          />
        </Group>

        <Group title="Templates">
          <Desc>
            Upload any designed images that your user will need to download for
            the challenge (Example: Habit Tracker Calendar, Meal Planner).{' '}
            <strong>You can upload up to 5 templates.</strong>
          </Desc>

          <div style={{ display: 'block' }}>
            <Button
              color="primaryTonal"
              variant="filled"
              size="medium"
              onClick={() => {
                p.dispatch('StartTemplateUpload');
              }}
              loading={p.isUploading}
            >
              Upload
            </Button>
            <input
              type="file"
              accept={'image/*'}
              ref={p.uploadInputRef}
              onChange={(ev) =>
                ev.target.files &&
                p.dispatch('UploadTemplate', ev.target.files[0])
              }
              multiple={false}
              hidden
            />
          </div>

          <TemplatesList
            urls={p.templates}
            onRemove={p.lazyDispatch('RemoveTemplate')}
          />
        </Group>
      </Col>
    </form>
  );
};
